/* eslint-disable unicorn/prefer-spread */
import { drawWatermark } from './draw'
import { observeIframes, observeWatermark } from './observers'
import type { WatermarkOptions } from './types'
import { findScrollParent, getElementRect, getPolygonWithRects, throttle } from './utils'

const DEFAULT_GAP = 100
const DEFAULT_OFFSET = 0

const defaults = {
  content: 'Transsion',
  color: 'rgba(0, 0, 0, 0.1)',
  fontSize: 14,
  lineHeight: 'auto',
  textAlign: 'center',
  fontFamily: 'sans-serif',
  fontStyle: 'normal',
  fontWeight: 400,
  gap: DEFAULT_GAP,
  height: 'auto',
  parentEl: document.body,
  rotate: -15,
  width: 'auto',
  offset: DEFAULT_OFFSET,
  zIndex: 9999,
  excludeIframe: false,
  scrollEl: 'auto',
} satisfies WatermarkOptions

function newWatermark({
  gap,
  offset,
  zIndex,
  ...restOptions
}: Required<Omit<WatermarkOptions, 'parentEl' | 'excludeIframe' | 'scrollEl'>>): HTMLDivElement {
  const [gapX = DEFAULT_GAP, gapY = DEFAULT_GAP] = Array.isArray(gap) ? gap : [gap, gap]
  const [offsetX = DEFAULT_OFFSET, offsetY = DEFAULT_OFFSET] = Array.isArray(offset)
    ? offset
    : [offset, offset]

  const { base64Url, size } = drawWatermark({ ...restOptions, gapX, gapY })

  const watermarkEl = document.createElement('div')

  watermarkEl.style.left = `${offsetX}px`
  watermarkEl.style.right = '0px'
  watermarkEl.style.top = `${offsetY}px`
  watermarkEl.style.bottom = '0px'
  watermarkEl.style.position = 'fixed'
  watermarkEl.style.pointerEvents = 'none'
  watermarkEl.style.zIndex = String(zIndex)
  watermarkEl.style.backgroundRepeat = 'repeat'
  watermarkEl.style.backgroundSize = `${size.width}px ${size.height}px`
  watermarkEl.style.backgroundImage = `url(${base64Url})`
  watermarkEl.style.backgroundPosition = `0px 0px`
  watermarkEl.style.width = '100%'
  watermarkEl.style.height = '100vh'
  watermarkEl.style.opacity = '1'
  watermarkEl.style.visibility = 'visible'
  watermarkEl.style.margin = '0px'
  watermarkEl.style.padding = '0px'

  return watermarkEl
}

export function registerWatermark(options: WatermarkOptions | string = {}): () => void {
  // 支持在 SSR 和测试环境下使用
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') {
    return () => {
      // noop
    }
  }

  const { excludeIframe, parentEl, scrollEl, ...restOptions } = {
    ...defaults,
    ...(typeof options === 'string' ? { content: options } : options),
  } satisfies WatermarkOptions

  const parentElValue = typeof parentEl === 'function' ? parentEl() : parentEl

  if (!parentElValue) {
    throw new TypeError('[TranWatermark] Parent element is required')
  }

  let watermarkEl: HTMLDivElement | null = null

  const addWatermark = () => {
    if (watermarkEl) return

    watermarkEl = newWatermark(restOptions)

    try {
      parentElValue.append(watermarkEl)
    } catch {
      // eslint-disable-next-line unicorn/prefer-dom-node-append
      parentElValue.appendChild(watermarkEl)
    }
  }

  const removeWatermark = () => {
    if (!watermarkEl) return

    try {
      watermarkEl.remove()
    } catch {
      // eslint-disable-next-line unicorn/prefer-dom-node-remove
      parentElValue.removeChild(watermarkEl)
    }

    watermarkEl = null
  }

  addWatermark()

  let iframeElementOb: MutationObserver | null = null
  let removeScrollListeners: (() => void) | null = null

  let updateClipPath: (() => void) | null = null
  let styleWithClipPath: string | null = null

  if (excludeIframe) {
    let iframes: HTMLIFrameElement[] = []

    updateClipPath = () => {
      if (!watermarkEl) return

      const rects = iframes.map((el) => getElementRect(el))

      const polygon = getPolygonWithRects(rects)

      watermarkEl.style.clipPath = polygon
      // 记录样式，避免 clipPath 导致的样式变化触发重绘
      styleWithClipPath = watermarkEl.style.cssText
    }

    const scrollHandler = throttle(() => {
      updateClipPath?.()
    }, 100)

    let scrollElement: Element = document.scrollingElement ?? document.documentElement

    const initScrollElements = () => {
      removeScrollListeners?.()

      removeScrollListeners = null

      if (scrollEl === 'auto') {
        iframes = Array.from(document.querySelectorAll('iframe'))

        const scrollElements: Element[] = iframes
          .map((el) => {
            const scrollParent = findScrollParent(el)

            return scrollParent ?? scrollElement
          })
          .concat(scrollElement)
          // 去重
          .filter((el, index, arr) => arr.indexOf(el) === index)

        for (const el of scrollElements) {
          el.addEventListener('scroll', scrollHandler)
          el.addEventListener('orientationchange', scrollHandler)
        }

        removeScrollListeners = () => {
          for (const el of scrollElements) {
            el.removeEventListener('scroll', scrollHandler)
            el.removeEventListener('orientationchange', scrollHandler)
          }
        }
      } else {
        const scrollElValue = typeof scrollEl === 'function' ? scrollEl() : scrollEl

        if (scrollElValue && scrollElValue !== window) {
          scrollElement = scrollElValue as HTMLElement
        }

        iframes = Array.from(scrollElement.querySelectorAll('iframe'))

        scrollElement.addEventListener('scroll', scrollHandler)
        scrollElement.addEventListener('orientationchange', scrollHandler)

        removeScrollListeners = () => {
          scrollElement.removeEventListener('scroll', scrollHandler)
          scrollElement.removeEventListener('orientationchange', scrollHandler)
        }
      }
    }

    initScrollElements()
    // 延迟执行，避免在初始化时触发多次
    scrollHandler()

    iframeElementOb = observeIframes(scrollElement, () => {
      initScrollElements()
      updateClipPath?.()
    })
  }

  let watermarkOb = observeWatermark(
    parentElValue,
    (node) => {
      return watermarkEl?.isEqualNode(node) ?? false
    },
    (isStyleAttribure = false) => {
      if (
        isStyleAttribure &&
        // 如果是 updateClipPath 导致的样式变化，不需要重新绘制
        styleWithClipPath &&
        watermarkEl?.style.cssText === styleWithClipPath
      ) {
        return
      }

      removeWatermark()
      addWatermark()
      updateClipPath?.()
    },
  )

  return () => {
    iframeElementOb?.disconnect()
    iframeElementOb = null

    watermarkOb?.disconnect()
    watermarkOb = null

    removeScrollListeners?.()
    removeScrollListeners = null

    removeWatermark()
  }
}

export type { WatermarkOptions }
